// Die Tafel — Kalender, Aufgabenliste, Termine und Fristen auf einer Fläche.
//
// Früher drei getrennte Ansichten (Heute, Board, Alle) und ein Formular mit
// allem auf einmal. Wer nur „Morgen 14:30 Meyer" festhalten wollte, musste
// sich durch Spalten, Prioritäten und Wiederholungen arbeiten.
//
// Jetzt eine Tafel wie an der Wand im Büro:
//   Woche   sieben Spalten, ein Zettel je Sache. Auf das „+" eines Tages
//           tippen, eine Zeile schreiben, fertig. Ziehen verschiebt.
//   Monat   der Kalender — ein Tag öffnet seine Zettel.
//   Liste   alles Offene nach Dringlichkeit, wie früher „Alle".
// Rechts daneben: was liegen geblieben ist (überfällig) und die Ablage —
// Zettel ohne Tag, „bei Gelegenheit".
//
// Auf der Tafel stehen auch die Zettel, die der Betrieb selbst stellt
// (Nachbestellen, Mahnen …) und Fristen aus den Daten (Rechnung fällig,
// Umsatzsteuer am 10.). Die sind berechnet, nicht gespeichert.
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../gemeinsam/lib/AuthContext';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import { Fenster } from '../../../gemeinsam/komponenten/Fenster';
import { Hinweis, Knopf, Spinner } from '../../../gemeinsam/komponenten/Basis';
import Icon from '../../../gemeinsam/komponenten/Icon';
import type { Aufgabe } from '../../../gemeinsam/lib/typen';
import {
  kalenderwoche, monatsRaster, nachTag, plus, sortiert, tag, wochenStart, wochenTage, type Eintrag,
} from '../lib/tafelDaten';
import { useTafel, useTafelTexte, fuellen, type Tafel } from '../lib/useTafel';
import { ART_STIL, NeuerZettel, Zettel } from '../komponenten/Zettel';
import { ZettelAnsehen, ZettelBearbeiten } from '../komponenten/ZettelFenster';

type Ansicht = 'woche' | 'monat' | 'liste';
const SPEICHER_MEINE = 'lp_aufgaben_meine';

export default function Aufgaben() {
  const { t, sprache } = useSprache();
  const { benutzer, flags } = useAuth();
  const [params, setParams] = useSearchParams();
  // Alte Adressen (?ansicht=alle aus Lesezeichen) landen in der passenden neuen Ansicht.
  const roh = params.get('ansicht') === 'alle' ? 'liste' : params.get('ansicht');
  const ansicht: Ansicht = (['woche', 'monat', 'liste'] as const).find(a => a === roh) ?? 'woche';
  const heute = tag(new Date());
  const bezug = /^\d{4}-\d{2}-\d{2}$/.test(params.get('tag') ?? '') ? params.get('tag')! : heute;
  const ich = benutzer?.id ?? '';

  const [nurMeine, setNurMeineRoh] = useState(() => { try { return localStorage.getItem(SPEICHER_MEINE) === '1'; } catch { return false; } });
  const setNurMeine = (w: boolean) => { setNurMeineRoh(w); try { localStorage.setItem(SPEICHER_MEINE, w ? '1' : '0'); } catch { /* egal */ } };

  // Zeitraum je Ansicht — die Fristen werden nur dafür berechnet.
  const tage = ansicht === 'woche' ? wochenTage(wochenStart(bezug)) : ansicht === 'monat' ? monatsRaster(bezug) : [];
  const von = ansicht === 'liste' ? heute : tage[0];
  const bis = ansicht === 'liste' ? plus(heute, 30) : tage[tage.length - 1];
  const tafel = useTafel({ von, bis });

  const [offen, setOffen] = useState<{ e?: Eintrag; neu?: string } | null>(null);
  const [tagFenster, setTagFenster] = useState<string | null>(null);

  const istMeine = (a?: Aufgabe) => !a || !nurMeine || !a.assigneeId || a.assigneeId === ich;
  const sichtbar = useMemo(() => tafel.eintraege.filter(e => istMeine(e.aufgabe)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tafel.eintraege, nurMeine, ich]);
  // Liegengebliebenes hängt rechts, nicht auf einem vergangenen Tag, den man nicht sieht.
  const liegen = sortiert(sichtbar.filter(e => e.quelle === 'eigen' && e.ueberfaellig));
  const ablage = sortiert(sichtbar.filter(e => e.quelle === 'eigen' && !e.datum && !e.fertig));
  const proTag = useMemo(() => nachTag(sichtbar.filter(e => e.datum && !(e.quelle === 'eigen' && e.ueberfaellig))), [sichtbar]);

  function gehe(neu: Partial<{ ansicht: Ansicht; tag: string }>) {
    setParams(p => {
      const n = new URLSearchParams(p);
      const a = neu.ansicht ?? ansicht; const d = neu.tag ?? bezug;
      if (a === 'woche') n.delete('ansicht'); else n.set('ansicht', a);
      if (d === heute) n.delete('tag'); else n.set('tag', d);
      return n;
    }, { replace: true });
  }
  const schritt = (r: 1 | -1) => {
    if (ansicht === 'monat') {
      const d = new Date(`${bezug.slice(0, 7)}-15T12:00:00`); d.setMonth(d.getMonth() + r); gehe({ tag: tag(d) });
    } else gehe({ tag: plus(bezug, 7 * r) });
  };

  function verschieben(schluessel: string, datum: string) {
    const e = tafel.eintraege.find(x => x.schluessel === schluessel);
    if (e?.aufgabe && e.datum !== datum) void tafel.aendern(e.aufgabe, { dueDate: datum });
  }
  const oeffnen = (e: Eintrag) => setOffen({ e });
  const erledigt = (e: Eintrag) => (fertig: boolean) => { if (e.aufgabe) void tafel.aendern(e.aufgabe, { column: fertig ? 'done' : 'todo' }); };

  if (tafel.aufgaben === null) return <div className="flex justify-center py-16"><Spinner /></div>;

  const titelZeitraum = ansicht === 'monat'
    ? new Date(`${bezug.slice(0, 7)}-15T12:00:00`).toLocaleDateString(sprache, { month: 'long', year: 'numeric' })
    : ansicht === 'woche'
      ? `${fuellen(t('tafel.kw'), { n: kalenderwoche(tage[0]) })} · ${new Date(`${tage[0]}T12:00:00`).toLocaleDateString(sprache, { day: 'numeric', month: 'short' })} – ${new Date(`${tage[6]}T12:00:00`).toLocaleDateString(sprache, { day: 'numeric', month: 'short', year: 'numeric' })}`
      : t('tafel.naechste30');
  const zeigtHeute = ansicht === 'liste' || tage.includes(heute) && (ansicht !== 'monat' || bezug.slice(0, 7) === heute.slice(0, 7));

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('tafel.titel')}</h1>
          <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-400">{t('tafel.untertitel')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div role="tablist" className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 dark:border-slate-700 dark:bg-slate-900">
            {(['woche', 'monat', 'liste'] as const).map(id => (
              <button key={id} role="tab" aria-selected={ansicht === id} onClick={() => gehe({ ansicht: id })}
                      className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${ansicht === id
                        ? 'bg-marke-600 text-white' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'}`}>
                {t(`tafel.ansicht.${id}`)}
              </button>
            ))}
          </div>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
            <input type="checkbox" checked={nurMeine} onChange={e => setNurMeine(e.target.checked)} />
            {t('aufgaben.nurMeine')}
          </label>
          <Knopf variante="primaer" onClick={() => setOffen({ neu: ansicht === 'liste' ? heute : tage.includes(heute) ? heute : tage[0] })}>
            <Icon name="plus" groesse={16} />{t('tafel.neuerZettel')}
          </Knopf>
        </div>
      </div>

      {tafel.fehler && <Hinweis art="fehler">{tafel.fehler}</Hinweis>}
      {tafel.tarifhinweis && <Hinweis art="warnung" titel={t('allg.nichtImTarif')}>{tafel.tarifhinweis}</Hinweis>}

      {ansicht !== 'liste' && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
            <button type="button" onClick={() => schritt(-1)} aria-label={t('tafel.zurueck')} className="rounded-s-lg p-1.5 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"><Icon name="pfeilLinks" groesse={18} /></button>
            <button type="button" onClick={() => gehe({ tag: heute })} disabled={zeigtHeute}
                    className="border-x border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:text-slate-400 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800">
              {t('aufgaben.heute')}
            </button>
            <button type="button" onClick={() => schritt(1)} aria-label={t('tafel.vor')} className="rounded-e-lg p-1.5 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"><Icon name="pfeilRechts" groesse={18} /></button>
          </div>
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{titelZeitraum}</p>
          <div className="ms-auto hidden items-center gap-3 text-xs text-slate-500 sm:flex dark:text-slate-400">
            {(['aufgabe', 'termin', 'frist'] as const).map(x => (
              <span key={x} className="inline-flex items-center gap-1"><span className={`h-2 w-2 rounded-full ${ART_STIL[x].punkt}`} />{t(`tafel.art.${x}`)}</span>
            ))}
          </div>
        </div>
      )}

      {/* Liegengebliebenes und Ablage: auf breiten Schirmen rechts neben der
          Woche, sonst darüber — die Woche braucht die Breite für ihre sieben Spalten. */}
      <div className="grid items-start gap-4 2xl:grid-cols-[1fr_17rem]">
        <aside className="grid items-start gap-3 sm:grid-cols-2 2xl:order-2 2xl:grid-cols-1">
          {liegen.length > 0 && (
            <Stapel titel={t('tafel.liegenGeblieben')} anzahl={liegen.length} warnung>
              {liegen.map(e => <Zettel key={e.schluessel} e={e} mitDatum ziehbar beiOeffnen={() => oeffnen(e)} beiErledigt={erledigt(e)} />)}
            </Stapel>
          )}
          <Ablage eintraege={ablage} tafel={tafel} beiOeffnen={oeffnen} beiErledigt={erledigt} beiVerschieben={verschieben} />
        </aside>

        <div className="min-w-0 2xl:order-1">
          {ansicht === 'woche' && (
            <Woche tage={tage} heute={heute} proTag={proTag} tafel={tafel}
                   beiOeffnen={oeffnen} beiErledigt={erledigt} beiVerschieben={verschieben} />
          )}
          {ansicht === 'monat' && (
            <Monat tage={tage} heute={heute} monat={bezug.slice(0, 7)} proTag={proTag} beiTag={setTagFenster} />
          )}
          {ansicht === 'liste' && (
            <Liste eintraege={sichtbar.filter(e => !e.fertig && e.datum && !(e.quelle === 'eigen' && e.ueberfaellig))} heute={heute}
                   beiOeffnen={oeffnen} beiErledigt={erledigt} />
          )}
        </div>
      </div>

      {tagFenster && (
        <Fenster beiSchliessen={() => setTagFenster(null)} breite="sm:max-w-md"
                 titel={new Date(`${tagFenster}T12:00:00`).toLocaleDateString(sprache, { weekday: 'long', day: 'numeric', month: 'long' })}>
          <div className="space-y-2 p-4">
            <ul className="space-y-2">
              {(proTag.get(tagFenster) ?? []).map(e => (
                <Zettel key={e.schluessel} e={e} beiOeffnen={() => { setTagFenster(null); oeffnen(e); }} beiErledigt={erledigt(e)} />
              ))}
            </ul>
            <NeuerZettel datum={tagFenster} team={tafel.team} beiAnlegen={tafel.anlegen} gross autoFokus />
          </div>
        </Fenster>
      )}

      {offen?.e?.aufgabe && (
        <ZettelBearbeiten key={offen.e.schluessel} a={offen.e.aufgabe} team={tafel.team} ich={ich}
                          mitTimer={flags?.advancedTasks !== false} tafel={tafel} beiSchliessen={() => setOffen(null)} />
      )}
      {offen?.e && !offen.e.aufgabe && <ZettelAnsehen e={offen.e} tafel={tafel} ich={ich} beiSchliessen={() => setOffen(null)} />}
      {offen?.neu !== undefined && (
        <ZettelBearbeiten a={null} datum={offen.neu} team={tafel.team} ich={ich}
                          mitTimer={false} tafel={tafel} beiSchliessen={() => setOffen(null)} />
      )}
    </div>
  );
}

// ── Bausteine ───────────────────────────────────────────────────────────────

/** Tafelgrund: feines Punktraster wie ein Whiteboard. */
const GRUND = 'bg-slate-50 bg-[radial-gradient(circle,rgba(100,116,139,0.16)_1px,transparent_1px)] [background-size:14px_14px] dark:bg-slate-900/60 dark:bg-[radial-gradient(circle,rgba(148,163,184,0.10)_1px,transparent_1px)]';

function Stapel({ titel, anzahl, warnung, hinweis, children, ...ziel }: {
  titel: string; anzahl: number; warnung?: boolean; hinweis?: string; children: React.ReactNode;
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div {...ziel} className={`rounded-xl border p-2.5 ${warnung ? 'border-red-200 bg-red-50/60 dark:border-red-900/60 dark:bg-red-950/20' : `border-slate-200 dark:border-slate-800 ${GRUND}`} ${ziel.className ?? ''}`}>
      <p className={`mb-2 flex items-center justify-between px-1 text-xs font-semibold uppercase tracking-wide ${warnung ? 'text-red-700 dark:text-red-400' : 'text-slate-600 dark:text-slate-400'}`}>
        {titel}<span className="font-normal">{anzahl}</span>
      </p>
      {hinweis && <p className="-mt-1 mb-2 px-1 text-[11px] text-slate-500 dark:text-slate-400">{hinweis}</p>}
      <ul className="space-y-2">{children}</ul>
    </div>
  );
}

function Ablage({ eintraege, tafel, beiOeffnen, beiErledigt, beiVerschieben }: {
  eintraege: Eintrag[]; tafel: Tafel; beiOeffnen: (e: Eintrag) => void;
  beiErledigt: (e: Eintrag) => (f: boolean) => void; beiVerschieben: (s: string, d: string) => void;
}) {
  const { t } = useSprache();
  const [ueber, setUeber] = useState(false);
  return (
    <Stapel titel={t('tafel.ablage')} anzahl={eintraege.length} hinweis={t('tafel.ablageHinweis')}
            onDragOver={e => { e.preventDefault(); setUeber(true); }} onDragLeave={() => setUeber(false)}
            onDrop={e => { e.preventDefault(); setUeber(false); beiVerschieben(e.dataTransfer.getData('text/plain'), ''); }}
            className={ueber ? 'ring-2 ring-marke-400' : ''}>
      {eintraege.map(e => <Zettel key={e.schluessel} e={e} ziehbar beiOeffnen={() => beiOeffnen(e)} beiErledigt={beiErledigt(e)} />)}
      <li><NeuerZettel datum="" team={tafel.team} beiAnlegen={tafel.anlegen} /></li>
    </Stapel>
  );
}

function Woche({ tage, heute, proTag, tafel, beiOeffnen, beiErledigt, beiVerschieben }: {
  tage: string[]; heute: string; proTag: Map<string, Eintrag[]>; tafel: Tafel;
  beiOeffnen: (e: Eintrag) => void; beiErledigt: (e: Eintrag) => (f: boolean) => void; beiVerschieben: (s: string, d: string) => void;
}) {
  return (
    // Unter etwa 1000 px Breite lieber seitlich scrollen als Zettel mit einem Wort je Zeile.
    <div className="-mx-1 overflow-x-auto px-1 pb-1">
    <div className="grid gap-2 lg:min-w-[60rem] lg:grid-cols-7">
      {tage.map(d => (
        <TagSpalte key={d} datum={d} heute={heute} eintraege={proTag.get(d) ?? []} tafel={tafel}
                   beiOeffnen={beiOeffnen} beiErledigt={beiErledigt} beiVerschieben={beiVerschieben} />
      ))}
    </div>
    </div>
  );
}

function TagSpalte({ datum, heute, eintraege, tafel, beiOeffnen, beiErledigt, beiVerschieben }: {
  datum: string; heute: string; eintraege: Eintrag[]; tafel: Tafel;
  beiOeffnen: (e: Eintrag) => void; beiErledigt: (e: Eintrag) => (f: boolean) => void; beiVerschieben: (s: string, d: string) => void;
}) {
  const { t, sprache } = useSprache();
  const [neu, setNeu] = useState(false);
  const [ueber, setUeber] = useState(false);
  const d = new Date(`${datum}T12:00:00`);
  const istHeute = datum === heute;
  const vorbei = datum < heute;
  const wochenende = d.getDay() === 0 || d.getDay() === 6;

  return (
    <section aria-label={d.toLocaleDateString(sprache, { weekday: 'long', day: 'numeric', month: 'long' })}
             onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setUeber(true); }}
             onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setUeber(false); }}
             onDrop={e => { e.preventDefault(); setUeber(false); beiVerschieben(e.dataTransfer.getData('text/plain'), datum); }}
             className={`group/tag flex flex-col rounded-xl border p-2 transition lg:min-h-[22rem] ${GRUND} ${istHeute
               ? 'border-marke-300 ring-1 ring-marke-300 dark:border-marke-800 dark:ring-marke-800'
               : 'border-slate-200 dark:border-slate-800'} ${ueber ? '!border-marke-500 ring-2 ring-marke-400' : ''} ${vorbei ? 'opacity-75' : ''}`}>
      <header className="mb-2 flex items-center justify-between px-1">
        <span className="flex items-baseline gap-1.5">
          <span className={`text-xs font-semibold uppercase tracking-wide ${istHeute ? 'text-marke-700 dark:text-marke-300' : wochenende ? 'text-slate-400' : 'text-slate-500 dark:text-slate-400'}`}>
            {d.toLocaleDateString(sprache, { weekday: 'short' })}
          </span>
          <span className={`text-sm font-bold ${istHeute ? 'rounded-full bg-marke-600 px-1.5 text-white' : 'text-slate-800 dark:text-slate-200'}`}>
            {d.getDate()}.{d.getMonth() + 1}.
          </span>
        </span>
        <button type="button" onClick={() => setNeu(true)} aria-label={`${t('tafel.neuerZettel')}: ${d.toLocaleDateString(sprache)}`}
                className="rounded-md p-0.5 text-slate-400 transition hover:bg-white hover:text-marke-600 lg:opacity-0 lg:group-hover/tag:opacity-100 lg:focus:opacity-100 dark:hover:bg-slate-800">
          <Icon name="plus" groesse={16} />
        </button>
      </header>
      <ul className="flex-1 space-y-2">
        {eintraege.map(e => (
          <Zettel key={e.schluessel} e={e} ziehbar={!!e.aufgabe} beiOeffnen={() => beiOeffnen(e)} beiErledigt={beiErledigt(e)} />
        ))}
        {neu && <li><NeuerZettel datum={datum} team={tafel.team} beiAnlegen={tafel.anlegen} autoFokus beiFertig={() => setNeu(false)} /></li>}
        {!neu && eintraege.length === 0 && (
          <li>
            <button type="button" onClick={() => setNeu(true)}
                    className="w-full rounded-lg border border-dashed border-slate-200 py-3 text-xs text-slate-400 hover:border-marke-300 hover:text-marke-600 lg:py-6 dark:border-slate-700">
              {t('tafel.leerTag')}
            </button>
          </li>
        )}
      </ul>
    </section>
  );
}

function Monat({ tage, heute, monat, proTag, beiTag }: {
  tage: string[]; heute: string; monat: string; proTag: Map<string, Eintrag[]>; beiTag: (d: string) => void;
}) {
  const { t, sprache } = useSprache();
  const { zettelTexte } = useTafelTexte();
  const kopf = tage.slice(0, 7).map(d => new Date(`${d}T12:00:00`).toLocaleDateString(sprache, { weekday: 'short' }));
  return (
    <div className={`overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 ${GRUND}`}>
      <div className="grid grid-cols-7 border-b border-slate-200 bg-white/70 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:bg-slate-900/70 dark:text-slate-400">
        {kopf.map(k => <div key={k} className="py-1.5">{k}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {tage.map(d => {
          const liste = proTag.get(d) ?? [];
          const fremd = d.slice(0, 7) !== monat;
          const istHeute = d === heute;
          return (
            <button key={d} type="button" onClick={() => beiTag(d)}
                    className={`flex min-h-[5.5rem] flex-col gap-0.5 border-b border-e border-slate-200 p-1 text-start transition hover:bg-white/80 sm:min-h-[7rem] dark:border-slate-800 dark:hover:bg-slate-800/60 ${fremd ? 'opacity-45' : ''}`}>
              <span className={`mb-0.5 self-start text-xs font-semibold ${istHeute ? 'rounded-full bg-marke-600 px-1.5 text-white' : 'text-slate-700 dark:text-slate-300'}`}>
                {new Date(`${d}T12:00:00`).getDate()}
              </span>
              {liste.slice(0, 3).map(e => (
                <span key={e.schluessel} className={`flex items-center gap-1 truncate rounded px-1 text-[11px] leading-4 ${ART_STIL[e.art].zettel} ${e.fertig ? 'line-through opacity-60' : ''}`}>
                  <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${ART_STIL[e.art].punkt}`} />
                  <span className="truncate text-slate-800 dark:text-slate-200">{e.uhrzeit && <b className="font-semibold">{e.uhrzeit} </b>}{zettelTexte(e).titel}</span>
                </span>
              ))}
              {liste.length > 3 && <span className="px-1 text-[11px] text-slate-500">{fuellen(t('tafel.weitere'), { n: liste.length - 3 })}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Liste({ eintraege, heute, beiOeffnen, beiErledigt }: {
  eintraege: Eintrag[]; heute: string; beiOeffnen: (e: Eintrag) => void; beiErledigt: (e: Eintrag) => (f: boolean) => void;
}) {
  const { t } = useSprache();
  const morgen = plus(heute, 1);
  const wocheEnde = plus(heute, 6);
  const gruppen: Array<[string, Eintrag[]]> = [
    [t('aufgaben.heute'), eintraege.filter(e => e.datum === heute)],
    [t('aufgaben.morgen'), eintraege.filter(e => e.datum === morgen)],
    [t('aufgaben.dieseWoche'), eintraege.filter(e => e.datum > morgen && e.datum <= wocheEnde)],
    [t('aufgaben.spaeter'), eintraege.filter(e => e.datum > wocheEnde)],
  ];
  if (!eintraege.length) {
    return <p className={`rounded-xl border border-slate-200 px-5 py-10 text-center text-sm text-slate-500 dark:border-slate-800 ${GRUND}`}>{t('aufgaben.allesErledigt')}</p>;
  }
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {gruppen.filter(([, l]) => l.length).map(([titel, l]) => (
        <Stapel key={titel} titel={titel} anzahl={l.length}>
          {sortiert(l).map(e => <Zettel key={e.schluessel} e={e} mitDatum={titel !== t('aufgaben.heute') && titel !== t('aufgaben.morgen')}
                                         beiOeffnen={() => beiOeffnen(e)} beiErledigt={beiErledigt(e)} />)}
        </Stapel>
      ))}
    </div>
  );
}
